import { useMemo } from 'react';

type SortDirection = 'asc' | 'desc';

type SortConfig = {
    [key: string]: SortDirection;
};

type TableSort = {
    key?: string;
    sortType?: 'ASC' | 'DESC';
};

type PrimitiveFilterValue = string | number | boolean | null | undefined;

type Payload = {
    page: number;
    size: number;
    globalSearch?: string;
    filters?: Record<string, PrimitiveFilterValue | PrimitiveFilterValue[]>;
    sort?: SortConfig | TableSort;
};

type UseLocalLoadFilterResult<T> = {
    data: T[];
    total: number;
};

export const useLocalLoadFilter = <T extends object>(allData: T[], payload: Payload): UseLocalLoadFilterResult<T> => {
    const { page, size, globalSearch, filters, sort } = payload;

    return useMemo(() => {
        let filtered = [...allData];

        // Global search — match against loadName, name, deviceName, loadDescription, description
        if (globalSearch?.trim()) {
            const search = globalSearch.toLowerCase();
            filtered = filtered.filter((item) => {
                const itemRecord = item as Record<string, unknown>;
                const loadName = String(itemRecord.loadName ?? itemRecord.name ?? '').toLowerCase();
                const deviceName = String(itemRecord.deviceName ?? '').toLowerCase();
                const description = String(itemRecord.loadDescription ?? itemRecord.description ?? '').toLowerCase();
                return loadName.includes(search) || deviceName.includes(search) || description.includes(search);
            });
        }

        // Column filters
        if (filters && Object.keys(filters).length) {
            Object.entries(filters).forEach(([key, rawValue]) => {
                if (rawValue === undefined || rawValue === null) return;

                const values = Array.isArray(rawValue) ? rawValue : [rawValue];
                if (!values.length || values.every((value) => String(value).trim() === '')) return;

                filtered = filtered.filter((item) => {
                    const itemValue = String((item as Record<string, unknown>)[key] ?? '').toLowerCase();
                    return values.some((value) => itemValue.includes(String(value).toLowerCase()));
                });
            });
        }

        // Sort
        if (sort && Object.keys(sort).length) {
            let sortKey = '';
            let sortDir: SortDirection = 'asc';

            // CustomTable sends { key, sortType }, while some server payloads may send { field: 'asc' | 'desc' }.
            if ('key' in sort || 'sortType' in sort) {
                sortKey = (sort as TableSort)?.key ?? '';
                sortDir = (sort as TableSort)?.sortType === 'DESC' ? 'desc' : 'asc';
            } else {
                const [fallbackSortKey, fallbackSortDir] = Object.entries(sort as SortConfig)[0];
                sortKey = fallbackSortKey;
                sortDir = fallbackSortDir;
            }

            if (!sortKey) {
                const total = filtered.length;
                const start = page * size;
                const paginated = filtered.slice(start, start + size);
                return { data: paginated, total };
            }

            filtered.sort((a, b) => {
                const aVal = String((a as Record<string, unknown>)[sortKey] ?? '').toLowerCase();
                const bVal = String((b as Record<string, unknown>)[sortKey] ?? '').toLowerCase();
                const comparison = aVal.localeCompare(bVal);
                return sortDir === 'desc' ? -comparison : comparison;
            });
        }

        // Pagination
        const total = filtered.length;
        const start = page * size;
        const paginated = filtered.slice(start, start + size);

        return { data: paginated, total };
    }, [allData, page, size, globalSearch, filters, sort]);
};
